import L from "leaflet";
import "leaflet-polylinedecorator";
import EventEmitter from "events";
import constants from "@/libs/Constants";
import ApiConnection from "@/libs/ApiConnection";
import IndexedDBStorage from '@/libs/IndexedDBStorage';
import {getStyleColorByVariable, mapLayersGetColorFromCSSVar} from "@/libs/Helpers";


export class LayerAreasSelectable extends EventEmitter {
    /**
     * Class constructor
     *
     * @param data {Object}
     * @param unit {String} Unit of measurement for layer
     * @param decimals {Number} Number of decimals to round
     * @param variablePalette {VariableColorConfigurationAbstract}
     * @param areaMultipleSelection {Boolean}
     * @param onClickFunction {Function}
     * @param onMouseOverFunction {Function}
     * @param onMouseOutFunction {Function}
     * @param onSelectAreasChangeFunction {Function}
     */
    constructor(
        data,
        unit,
        decimals,
        variablePalette = null,
        areaMultipleSelection = true,
        onClickFunction = null,
        onMouseOverFunction = null,
        onMouseOutFunction = null,
        onSelectAreasChangeFunction = null
    ) {
        super();

        //List of municipalities
        this.municipalities = data.municipalities !== undefined ? data.municipalities : {};

        //Unit of measurement
        this.unit = unit;

        //Number of decimals
        this.decimals = decimals;

        this.selected = {};
        this.layerIndex = {};

        this.layerDefault = {
            fillOpacity: 0.6,
            color: getStyleColorByVariable('--layer-default'),
            weight: 2,
            opacity: 1,
        };


        //Retrieve minimum and maximum values from data and keep information of features
        let minValueData = null;
        let maxValueData = null;
        this.featureData = {};

        if (data.features) {
            data.features.forEach(feature => {
                let featureValueFloat = null;

                //Value
                let featureValue = feature.properties.value;
                if (featureValue !== undefined && featureValue !== null) {
                    /*Zero values can be recognized as null, parse value before*/
                    featureValueFloat = parseFloat(featureValue);
                    if (isNaN(featureValueFloat))
                        featureValueFloat = null;
                }

                if (featureValueFloat !== null) {
                    if (minValueData === null || featureValueFloat < minValueData)
                        minValueData = featureValueFloat;
                    if (maxValueData === null || featureValueFloat > maxValueData)
                        maxValueData = featureValueFloat;

                }

                //Altitude
                let areaAltitudeFloat = null;
                let areaAltitude = feature.properties.altitude_average;
                if (areaAltitude !== undefined && areaAltitude !== null) {
                    /*Zero values can be recognized as null, parse value before*/
                    areaAltitudeFloat = parseFloat(areaAltitude);
                    if (isNaN(areaAltitudeFloat))
                        areaAltitudeFloat = null;
                }

                //Municipalities
                let featureMunicipalitiesStr = null;
                let featureMunicipalitiesList = feature.properties.municipalities;
                if (featureMunicipalitiesList !== undefined && featureMunicipalitiesList !== null)
                    featureMunicipalitiesStr = this.layerMunicipalityNamesStrFromList(featureMunicipalitiesList);

                let featureId = feature.properties.id;
                this.featureData[featureId] = {
                    name: feature.properties.name || null,
                    value: featureValueFloat,
                    altitude: areaAltitudeFloat,
                    municipalities: featureMunicipalitiesStr
                };
            });
        }

        let colorDataArtifact = null;
        let colorScaleFunction = null;
        if (variablePalette !== null && variablePalette !== undefined) {
            colorDataArtifact = variablePalette.getLegendColorArtifact(
                minValueData,
                maxValueData
            );

            //Set values from color configuration
            colorScaleFunction = colorDataArtifact.chromaJSColorFunction;
        }

        let layerDefault = this.layerDefault;

        //Set layer options for interaction with Leaflet
        let layerOptions = {
            style: (feature) => {
                let fillColor = typeof colorScaleFunction === "function" ?
                    colorScaleFunction(feature.properties.value) : // use color scale defined from
                    getStyleColorByVariable('--layer-bg-default'); // default color for features

                //Use default for layer (clone it to assign fill color)
                let layerDefaultCp = Object.assign({}, layerDefault);

                //Change fill color based on range
                layerDefaultCp.fillColor = fillColor;

                return layerDefaultCp;
            },

            onEachFeature: (feature, layer) => {
                let featureId = feature.properties.id;

                //Store layer in list
                this.layerIndex[featureId] = layer;

                layer.on('click', () => {
                    /**
                     * Event on map's layer click
                     * Keep it in list of selected layers
                     */
                    if (this.layerIsSelected(featureId)) {
                        this.layerMarkAsUnselected(layer);
                        this.layerRemoveFromSelected(featureId);
                    } else {
                        /*If layer does not allow multiple selection, clear all and select only the clicked one*/
                        if (areaMultipleSelection === false)
                            this.layersClearAll();

                        //Add layer to list
                        this.layerAddAsSelected(featureId, layer);
                        this.layerMarkAsSelected(layer);
                    }

                    if (typeof onClickFunction === "function")
                        onClickFunction(featureId);
                });

                layer.on('mouseover', () => {
                    /**
                     * Set properties from map layer
                     * to show in information box on hover
                     */
                    if (typeof onMouseOverFunction === "function")
                        onMouseOverFunction(featureId);

                    //Highlight value
                    if (!this.layerIsSelected(featureId))
                        this.layerMarkAsHighlighted(layer);
                    else
                        this.layerMarkAsUnhighlighted(layer);
                });

                layer.on('mouseout', () => {
                    if (!this.layerIsSelected(featureId))
                        this.layerMarkAsUnselected(layer);
                    else
                        this.layerMarkAsSelected(layer);

                    if (typeof onMouseOutFunction === "function")
                        onMouseOutFunction();
                });
            }
        }

        this._layer = L.geoJSON(data, layerOptions);
        this._legendColorArtifactObject = colorDataArtifact;

        //Set function on update the list of selected layers
        if (typeof onSelectAreasChangeFunction === "function")
            this.on('onSelectedChange', onSelectAreasChangeFunction);
    }

    layerMunicipalityNamesStrFromList(municipalities) {
        let municipalityNames = [];
        municipalities.forEach(municipality => {
            if (this.municipalities.hasOwnProperty(municipality))
                municipalityNames.push(this.municipalities[municipality]);
        });
        if (municipalityNames.length > 0)
            return municipalityNames.sort().join(', ');
        else
            return null;
    }

    layerIsSelected(id) {
        return this.selected.hasOwnProperty(id);
    }

    layerAddAsSelected(id, layer) {
        this.selected[id] = layer;
        this.emitOnSelectedChange();
    }

    layerRemoveFromSelected(id) {
        if (this.layerIsSelected(id))
            delete this.selected[id];
        this.emitOnSelectedChange();
    }

    layerMarkAsSelected(layer) {
        layer.setStyle({
            weight: 4,
            fillOpacity: 1,
            color: getStyleColorByVariable('--layer-selected')
        });
    }

    layerMarkAsHighlighted(layer) {
        layer.setStyle({
            weight: 5,
            fillOpacity: 1,
            color: getStyleColorByVariable('--layer-mouseover-highlight')
        });
    }

    layerMarkAsUnhighlighted(layer) {
        layer.setStyle({
            weight: 5,
            fillOpacity: 1,
            color: getStyleColorByVariable('--layer-mouseover-unhighlight'),
        });
    }

    layerMarkAsUnselected(layer) {
        layer.setStyle(this.layerDefault);
    }

    layersClearAll() {
        Object.keys(this.selected).forEach(featureId => {
            let layerRemove = this.selected[featureId];
            this.layerMarkAsUnselected(layerRemove);
        })
        this.selected = {};

        this.emitOnSelectedChange();
    }

    layersSelectAll() {
        for (const [id, layer] of Object.entries(this.layerIndex)) {
            if (!this.layerIsSelected(id)) {
                this.layerAddAsSelected(id, layer);
                this.layerMarkAsSelected(layer);
            }
        }
    }

    emitOnSelectedChange() {
        this.emit('onSelectedChange', this.layersSelectedCount);
    }


    featureValueRound(id) {
        let featureData = this.featureData[id];

        let value = '';
        if (featureData !== undefined) {
            let valueFl = parseFloat(featureData.value);
            if (!isNaN(valueFl))
                value = valueFl.toFixed(this.decimals);
            else
                value = null;
        }
        return value;
    }

    featureAreaName(id) {
        let featureData = this.featureData[id];

        let value = '';
        if (featureData !== undefined)
            value = featureData.name;

        return value;
    }

    featureAltitudeRound(id) {
        let featureData = this.featureData[id];

        let value = 'NaN';
        if (featureData !== undefined) {
            let valueFl = parseFloat(featureData.altitude);
            if (!isNaN(valueFl))
                value = valueFl.toFixed(0);
        }
        return value;
    }

    featureMunicipalities(id) {
        let featureData = this.featureData[id];

        let value = '';
        if (featureData !== undefined)
            value = featureData.municipalities;

        return value;
    }

    featureValueUnitStr(id, i18n) {
        let str = this.featureValueRound(id);
        if (str === null) {
            str = i18n.t("Units.NotAvailable");
        } else {
            if (this.unit !== undefined && this.unit !== null && this.unit !== '')
                str = `${str} ${this.unit}`;
        }

        return str;
    }

    /***********
     * Getters *
     ***********/
    get layersCount() {
        return Object.keys(this.layerIndex).length
    }

    get layersSelectedCount() {
        return Object.keys(this.selected).length
    }

    get layersSelectedIdsSorted() {
        let itemsStr = [];
        Object.keys(this.selected).forEach(item => {
            itemsStr.push(item.toString());
        })
        return itemsStr.sort();
    }

    get layerSelectedOneName() {
        let layerName = '';

        let selectedKeys = Object.keys(this.selected);
        if (selectedKeys.length === 1) {
            let featureId = selectedKeys[0];
            layerName = this.featureAreaName(featureId);
        }
        return layerName;
    }

    get layerSelectedNameList() {
        let layerNames = [];
        Object.keys(this.selected).forEach(item => {
            layerNames.push(this.featureAreaName(item))
        })
        return layerNames.sort();
    }


    layersSelectedAverage(attribute, decimals = 0) {
        let valueTotal = 0.0;
        let valueCount = 0;

        Object.keys(this.selected).forEach(featureId => {
            let layerData = this.featureData[featureId];
            let layerValue = layerData !== undefined ? layerData[attribute] : null;

            if (layerValue !== undefined && layerValue !== null) {
                /*Zero values can be recognized as null, parse value before*/
                valueTotal += layerValue;
                valueCount++;
            }
        });

        return valueCount > 0 ? (valueTotal / valueCount).toFixed(decimals) : null;
    }

    get layersSelectedValueAverage() {
        return this.layersSelectedAverage('value', 0);
    }

    get layersSelectedAltitudeAverage() {
        return this.layersSelectedAverage('altitude', 0);
    }

    get layerLeaflet() {
        return this._layer;
    }

    /**
     * Retrieve artifact with function from Chroma.js to define color of layer elements
     * @returns {LegendColorArtifact}
     */
    get legendColorArtifactObject() {
        return this._legendColorArtifactObject;
    }
}

/* -------
 * Leaflet
 * ------- */
/**
 * Create a GeoJSON object in Leaflet based on a layer type
 *
 * @param layerType {String} Type of layer to set options
 * @param data {Object} GeoJSON data to load
 * @param onEachFeatureFunc {Function} Function to run on each feature
 * @returns {*|null} Object or null if no layerType found
 */
function leafletCreateLayer(layerType, data, onEachFeatureFunc = null) {
    let geoJsonOptions = null;
    let colorArea = mapLayersGetColorFromCSSVar(layerType);

    switch (layerType) {
        case constants.LAYERS.AREA:
            geoJsonOptions = {
                style: {
                    fillColor: colorArea,
                    color: colorArea,
                    weight: 6,
                    opacity: 0.7,
                    fillOpacity: 0
                },
                interactive: false
            };
            break;
        case constants.LAYERS.MUNICIPALITIES:
            geoJsonOptions = {
                style: (feature) => {
                    return {
                        color: colorArea,
                        opacity: 1,
                        fillColor: getStyleColorByVariable('--municipalities-bg-color'),
                        fillOpacity: 0.6,
                        weight: 2
                    };
                },
                interactive: false
            };
            break;
        case constants.LAYERS.RIVER:
        case constants.LAYERS.RIVERS_DETAIL:
            geoJsonOptions = {
                style: {
                    color: colorArea,
                    weight: 2,
                    dashArray: 0,
                    opacity: 1,
                },
                interactive: false
            };
            break;

        case constants.LAYERS.DERIVATION_DIRECTION:
            geoJsonOptions = {
                style: {
                    color: colorArea,
                    weight: 4,
                    dashArray: 0,
                    opacity: 1,
                },
                interactive: false
            };
            break;
        case constants.LAYERS.AGRICULTURE_AREA:
            geoJsonOptions = {
                style: {
                    color: colorArea,
                    weight: 2,
                    dashArray: 0,
                    opacity: 1,
                    fillOpacity: 0.7
                },
                interactive: false
            };
            break;
        case constants.LAYERS.LAKES:
            geoJsonOptions = {
                style: {
                    color: colorArea,
                    weight: 2,
                    dashArray: 0,
                    fillOpacity: 0.8,
                    opacity: 1,
                }
            };
            break;
        case constants.LAYERS.GLACIER:
            geoJsonOptions = {
                style: {
                    color: getStyleColorByVariable('--anthropic-work-river-color'),
                    fillColor: colorArea,
                    weight: 2,
                    dashArray: [3, 3],
                    opacity: 1,
                    fillOpacity: 0.6,
                }
            };
            break;
        case constants.LAYERS.DERIVATION_POINTS:
            geoJsonOptions = {
                pointToLayer: (feature, latLong) => {
                    return L.circleMarker(latLong, {
                        radius: 3,
                        fillColor: colorArea,
                        color: '#FFF',
                        weight: 2,
                        opacity: 1,
                        fillOpacity: 0.9,
                    });
                }
            }
            break;
    }

    if (geoJsonOptions !== null && onEachFeatureFunc !== null)
        geoJsonOptions.onEachFeature = onEachFeatureFunc;

    return geoJsonOptions !== null ?
        {
            object: L.geoJSON(data, geoJsonOptions),
            count: data.features.length
        } : null;
}

/**
 * Given a key and some data, store it in local memory if environment variable is set
 * @param key
 * @param data
 */
async function saveGeoJSONDataLayerInStorage(key, data) {
    let secondsStorage = parseInt(process.env.VUE_APP_KEEP_LAYER_GEOJSON_IN_CACHE_SECONDS);
    if (isNaN(secondsStorage) === false && secondsStorage > 0)
        await IndexedDBStorage.set(key, data, secondsStorage);
}

/**
 * Create style objects for water infrastructures
 *
 * @param layerType {String} Layer type (water structure)
 * @returns {{styleFunction: null, stylePointFunction: null, mouseOverStyle: {color: string | string}, mouseOutStyle: {color: string, weight: number}}}
 */
export function mapLayerWaterStructureStyle(layerType) {
    let styleFunction = null;
    let stylePointFunction = null;

    let colorValue = mapLayersGetColorFromCSSVar(layerType);

    let mouseOutStyle = {color: 'white', weight: 2};
    let mouseOverStyle = {color: getStyleColorByVariable('--layer-mouseover-highlight')};

    switch (layerType) {
        case constants.LAYERS.DAM_LAKE:
        case constants.LAYERS.PLANT:
            stylePointFunction = function (latLong) {
                return L.circleMarker(latLong, {
                    radius: 9,
                    fillColor: colorValue,
                    color: mouseOutStyle.color,
                    weight: mouseOutStyle.weight,
                    opacity: 1,
                    fillOpacity: 0.9,
                });
            };
            break;

        case constants.LAYERS.DERIVATION:
            mouseOutStyle.color = colorValue;
            mouseOutStyle.weight = 6;

            styleFunction = {
                color: mouseOutStyle.color,
                borderColor: '#FFF',
                weight: mouseOutStyle.weight,
            };
            break;

        case constants.LAYERS.RIVER:
            mouseOutStyle.color = colorValue;
            mouseOutStyle.weight = 3;

            styleFunction = {
                color: mouseOutStyle.color,
                weight: mouseOutStyle.weight,
                dashArray: '5',
                dashOffset: '3',
            };
            break;
    }

    mouseOverStyle.weight = mouseOutStyle.weight + 2;

    return {
        styleFunction: styleFunction,
        stylePointFunction: stylePointFunction,
        mouseOverStyle: mouseOverStyle,
        mouseOutStyle: mouseOutStyle,
        colorValue: colorValue
    };
}

/* -------
 * Loaders
 * ------- */
export function layerMapOSMLoad(minZoom = 9, maxZoom = 12, grayscale = false) {
    let url = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';

    return L.tileLayer(url, {
        attribution: '&copy; OpenStreetMap contributors',
        minZoom: minZoom,
        maxZoom: maxZoom,
        className: grayscale === true ? 'bw' : 'osm'
    });
}

/**
 * Load layer from IndexedDB or from API and create a GeoJSON object for Leaflet
 *
 * @param layerType Type of layer to load
 * @param saveInCache Whether to store result in cache or not
 * @param onEachFeatureFunc Function to run on each feature, null if not necessary
 * @returns {Promise<*|null>}
 */
export async function layerLoadWrapper(layerType, saveInCache = true, onEachFeatureFunc = null) {
    let layerMap = {
        [constants.LAYERS.AREA]: 'basin',
        [constants.LAYERS.MUNICIPALITIES]: 'municipalities',
        [constants.LAYERS.RIVER]: 'simulation_rivers',
        [constants.LAYERS.RIVERS_DETAIL]: 'rivers_detail',
        [constants.LAYERS.GLACIER]: 'glaciers',
        [constants.LAYERS.LAKES]: 'lakes',
        [constants.LAYERS.AGRICULTURE_AREA]: 'agriculture_areas',
        [constants.LAYERS.DERIVATION_DIRECTION]: 'simulation_derivations_direction',
        [constants.LAYERS.DERIVATION_POINTS]: 'simulation_derivation_points'
    };

    if (layerMap.hasOwnProperty(layerType)) {
        let layerDownload = layerMap[layerType];
        let storageKey = `layer_${layerDownload}`;

        let data = saveInCache === true ? await IndexedDBStorage.get(storageKey) : null;

        if (data === null) {
            return await ApiConnection.getConfigurationLayers(layerDownload).then(response => {
                return response.json().then(async data => {
                    //Store in memory
                    if (saveInCache === true)
                        await saveGeoJSONDataLayerInStorage(storageKey, data);

                    return leafletCreateLayer(layerType, data, onEachFeatureFunc);
                });
            }).catch(error => {
                return null;
            });
        } else {
            return leafletCreateLayer(layerType, data, onEachFeatureFunc);
        }
    }
    return null;
}

export async function layerSimulationLoad(type) {
    let layerApiName = null;
    switch (type) {
        case constants.LAYERS.DAM_LAKE:
            layerApiName = 'simulation_dams_lakes';
            break;
        case constants.LAYERS.DERIVATION:
            layerApiName = 'simulation_derivations';
            break;
        case constants.LAYERS.PLANT:
            layerApiName = 'simulation_hydropower_plants';
            break;
    }

    if (layerApiName !== null) {
        let storageKey = `layer_${layerApiName}`;
        let data = await IndexedDBStorage.get(storageKey);

        if (data !== null)
            return data;

        return await ApiConnection.getConfigurationLayers(layerApiName).then(response => {
            return response.json().then(async data => {
                //Store in memory
                await saveGeoJSONDataLayerInStorage(storageKey, data);
                return data;
            })
        }).catch(error => {
            return null;
        });
    } else
        return null;
}

/**
 * Create arrows for layer containing lines for derivation directions
 *
 * @param layerData {Object} Leaflet GeoJSON data
 * @returns {*} New layer in Leaflet
 */
export function layerDerivationCreateDirectionArrows(layerData) {
    return L.polylineDecorator(layerData.getLayers(), {
        patterns: [
            {
                offset: '100%',         // position of arrow along the line
                repeat: 0,             // no repetition, only one arrow
                symbol: L.Symbol.arrowHead({
                    pixelSize: 13,
                    polygon: false,
                    pathOptions: {
                        stroke: true,
                        color: mapLayersGetColorFromCSSVar(constants.LAYERS.DERIVATION_DIRECTION),
                        weight: 4,
                        fillOpacity: 1
                    }
                })
            }
        ]
    });
}